/**
 * DirectionResolver - Maps character facing angle and camera angle to 8-directional sprite direction
 *
 * The 8 sprite directions correspond to compass points:
 * - south (toward camera)
 * - south-east, east, north-east
 * - north (away from camera)
 * - north-west, west, south-west
 */
export default class DirectionResolver {
  // The 8 directions in order, starting from east (0 degrees) and going counter-clockwise
  static DIRECTIONS = [
    'east',       // 0° (facing right)
    'north-east', // 45°
    'north',      // 90° (facing away)
    'north-west', // 135°
    'west',       // 180° (facing left)
    'south-west', // 225°
    'south',      // 270° (facing toward camera)
    'south-east'  // 315°
  ];

  // Angle thresholds for each direction (22.5 degrees each side of center)
  static ANGLE_STEP = 45;
  static HALF_STEP = 22.5;
  // Hysteresis threshold: must exceed this many degrees past boundary to switch direction
  // Prevents oscillation when facing angle hovers near a direction boundary
  static HYSTERESIS = 8;

  /**
   * Resolve which sprite direction to use based on character facing and camera angle
   * @param {number} characterFacingAngle - The angle the character is facing (0-360 degrees)
   * @param {number} cameraAngle - The camera's rotation angle (0-360 degrees)
   * @returns {string} One of the 8 direction strings
   */
  static resolve(characterFacingAngle, cameraAngle) {
    // Calculate the relative angle (character facing relative to camera view)
    // When camera rotates, we want the sprite to appear to rotate with it
    const relativeAngle = ((characterFacingAngle - cameraAngle) % 360 + 360) % 360;

    // Snap to nearest 45-degree direction
    // Add half step to center the ranges (so 0±22.5 maps to index 0)
    const index = Math.round(relativeAngle / this.ANGLE_STEP) % 8;

    return this.DIRECTIONS[index];
  }

  /**
   * Get the direction index (0-7) for a given angle
   * @param {number} characterFacingAngle - The angle the character is facing
   * @param {number} cameraAngle - The camera's rotation angle
   * @returns {number} Direction index (0-7)
   */
  static resolveIndex(characterFacingAngle, cameraAngle) {
    const relativeAngle = ((characterFacingAngle - cameraAngle) % 360 + 360) % 360;
    return Math.round(relativeAngle / this.ANGLE_STEP) % 8;
  }

  /**
   * Get the direction string for a given index
   * @param {number} index - Direction index (0-7)
   * @returns {string} Direction string
   */
  static getDirectionByIndex(index) {
    return this.DIRECTIONS[index % 8];
  }

  /**
   * Get the angle for a given direction
   * @param {string} direction - Direction string (e.g., 'north', 'south-east')
   * @returns {number} Angle in degrees (0-360)
   */
  static getAngleForDirection(direction) {
    const index = this.DIRECTIONS.indexOf(direction);
    if (index === -1) return 0;
    return index * this.ANGLE_STEP;
  }

  /**
   * Get the opposite direction
   * @param {string} direction - Current direction
   * @returns {string} Opposite direction
   */
  static getOppositeDirection(direction) {
    const index = this.DIRECTIONS.indexOf(direction);
    if (index === -1) return 'south';
    return this.DIRECTIONS[(index + 4) % 8];
  }

  /**
   * Calculate the facing angle from a movement vector
   * @param {number} dx - Movement in X direction
   * @param {number} dz - Movement in Z direction
   * @returns {number} Facing angle in degrees (0-360)
   */
  static angleFromMovement(dx, dz) {
    if (dx === 0 && dz === 0) return null; // No movement

    // atan2 returns angle in radians, convert to degrees
    // atan2(y, x) where y is "up" and x is "right"
    // In our coordinate system: +X is east, +Z is north
    const radians = Math.atan2(dz, dx);
    const degrees = radians * (180 / Math.PI);

    // Normalize to 0-360
    return ((degrees % 360) + 360) % 360;
  }

  /**
   * Convert a direction string to a movement vector (unit vector)
   * @param {string} direction - Direction string
   * @returns {Object} Unit vector {x, z}
   */
  static directionToVector(direction) {
    const vectors = {
      'east':       { x: 1, z: 0 },
      'north-east': { x: Math.SQRT1_2, z: Math.SQRT1_2 },
      'north':      { x: 0, z: 1 },
      'north-west': { x: -Math.SQRT1_2, z: Math.SQRT1_2 },
      'west':       { x: -1, z: 0 },
      'south-west': { x: -Math.SQRT1_2, z: -Math.SQRT1_2 },
      'south':      { x: 0, z: -1 },
      'south-east': { x: Math.SQRT1_2, z: -Math.SQRT1_2 }
    };
    return vectors[direction] || { x: 0, z: 0 };
  }
}
